import { useEffect, useState } from 'react';
import { Modal, Stack, Text, Button, Group, Collapse, Code, Anchor, Alert } from '@mantine/core';
import { IconAlertTriangle, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

export interface SyncErrorInfo {
  submissionId: string;
  formType: string;
  /** Menschenlesbare Erklärung (kein Prisma-/Server-Jargon). */
  humanMessage: string;
  /** Rohe Server-Fehlermeldung — nur auf Wunsch sichtbar. */
  technicalDetail?: string;
}

/**
 * Global gemountetes Fehler-Modal für Offline-Sync-Probleme.
 *
 * Toasts sind für diese Fehler zu klein: die Server-Ursache ist oft technisch
 * (Prisma-/Validierungsprosa) und gehört hinter ein "Technische Details" —
 * vorn steht eine Erklärung in Alltagssprache plus der Weg zum Bericht.
 */
let opener: ((info: SyncErrorInfo) => void) | null = null;

export function openSyncErrorDialog(info: SyncErrorInfo): void {
  opener?.(info);
}

export function SyncErrorDialog() {
  const [info, setInfo] = useState<SyncErrorInfo | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    opener = (i) => {
      setInfo(i);
      setShowDetails(false);
    };
    return () => {
      opener = null;
    };
  }, []);

  const reportRoute =
    info?.formType === 'ONSITE_DELIVERY'
      ? `/delivery-report/${info?.submissionId}`
      : `/service-report/${info?.submissionId}`;

  return (
    <Modal
      opened={info !== null}
      onClose={() => setInfo(null)}
      title="Synchronisierung fehlgeschlagen"
      size="lg"
      centered
    >
      {info && (
        <Stack gap="md">
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">{info.humanMessage}</Text>
          </Alert>
          <Text size="sm" c="dimmed">
            Der Berichts-Entwurf ist lokal weiterhin vorhanden und geht nicht verloren.
            Die Synchronisierung wird automatisch erneut versucht — oder öffne den
            Bericht, prüfe die Eingaben und speichere erneut.
          </Text>

          {info.technicalDetail && (
            <div>
              <Anchor
                component="button"
                type="button"
                size="xs"
                onClick={() => setShowDetails((v) => !v)}
              >
                <Group gap={4} component="span">
                  {showDetails ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
                  Technische Details {showDetails ? 'ausblenden' : 'anzeigen'}
                </Group>
              </Anchor>
              <Collapse expanded={showDetails}>
                <Code block mt="xs" style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
                  {info.technicalDetail}
                </Code>
              </Collapse>
            </div>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setInfo(null)}>
              Schließen
            </Button>
            <Button
              onClick={() => {
                setInfo(null);
                navigate(reportRoute);
              }}
            >
              Bericht öffnen
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
